/**
 * Anomalieerkennung page (`/anomaly-detection`).
 *
 * Wires the React anomaly-pipeline FSM (`useAnomalyPipeline`) into the page:
 *   1. File upload → chunked POST to /api/adjustmentsOfData/upload-chunk
 *   2. On upload complete → automatic POST /load (renders original + slope plot)
 *   3. Parameter form (5 progressive-disclosure groups, Markus L731-771)
 *   4. "Daten verarbeiten" → POST /start
 *   5. STL/LSTM threshold pauses → ThresholdInputModal via PipelineRunner
 *   6. Complete → AnomalyResultsSection (overlay plots + Loading processed data)
 *   Download CSV is a persistent action-bar button, available whenever a processed file exists.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { log } from '@/core/debug/anomalyLogger';
import { useQuotaCheck } from '@/features/subscription/hooks/useQuotaCheck';
import UpgradeModal from '@/features/subscription/components/UpgradeModal';

import ErrorBoundary from '../../shared/components/errors/ErrorBoundary';
import NetworkStatusIndicator from '../../shared/components/feedback/NetworkStatusIndicator';
import WorkflowStepper from '../../shared/components/layout/WorkflowStepper';
import ErrorMessage from '../../shared/components/feedback/ErrorMessage';
import SuccessMessage from '../../shared/components/feedback/SuccessMessage';
import FileUploadSection from '../../features/data-upload/components/FileUploadSection';
import ProgressTracker from '../../shared/components/feedback/ProgressTracker';
import { useAnomalyProgress } from '../../features/data-processing/anomaly/useAnomalyProgress';
import { useAnomalySocket } from '../../features/data-processing/anomaly/useAnomalySocket';

import AnomalyParametersSection from '../../features/data-processing/anomaly/components/AnomalyParametersSection';
import OriginalDataSection from '../../features/data-processing/anomaly/components/OriginalDataSection';
import LoadedFileInfo from '../../features/data-processing/anomaly/components/LoadedFileInfo';
import PipelineRunner from '../../features/data-processing/anomaly/components/PipelineRunner';
import AnomalyResultsSection from '../../features/data-processing/anomaly/components/AnomalyResultsSection';
import { useAnomalyPipeline } from '../../features/data-processing/anomaly/useAnomalyPipeline';
import { useAnomalyUpload } from '../../features/data-processing/anomaly/useAnomalyUpload';
import { useAnomalyDownload } from '../../features/data-processing/anomaly/useAnomalyDownload';
import { ANOMALY_DEFAULTS, AnomalyParameters } from '../../features/data-processing/anomaly/types';

import { useKeepAlive } from '@/core/services/keepalive';
import { mapAdjustmentsBackendError } from '@/features/data-adjustments/utils/adjustmentsBackendErrorMapper';
import { WORKFLOW_STEPS } from '@/features/data-processing/types/dataProcessing.types';
import {
  AUTO_DISMISS_SUCCESS_MS,
  AUTO_DISMISS_ERROR_MS,
} from '../../constants/dataProcessing.constants';
import SubmitButton from '@/shared/components/buttons/SubmitButton';
import RestartButton from '@/shared/components/buttons/RestartButton';
import { ROUTES } from '@/constants/routes';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';
import { usePublishChatContext } from '@/features/chatbot/usePublishChatContext';
import type { ChatField } from '@/features/chatbot/types';

const STALE_PROGRESS_MS = 30_000;

const AnomalyDetectionPage: React.FC = () => {
  const { t, i18n } = useTranslation('dataProcessing');
  const lang: 'en' | 'de' = i18n?.language === 'de' ? 'de' : 'en';

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [params, setParams] = useState<AnomalyParameters>(ANOMALY_DEFAULTS);
  const [success, setSuccess] = useState<string | null>(null);

  const { canProcess, refreshUsage } = useQuotaCheck();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  // Publish current anomaly-detection settings to the chatbot assistant context
  const chatContext = useMemo(() => {
    const fields: ChatField[] = [];
    fields.push({ label: 'Saisonalität (STL)', value: params.stl.run ? 'Ja' : 'Nein' });
    if (params.stl.periodH) fields.push({ label: 'STL-Periodendauer [h]', value: params.stl.periodH });
    fields.push({ label: 'LSTM aktiv', value: params.lstm.run ? 'Ja' : 'Nein' });
    if (params.vMax) fields.push({ label: 'Max. Wert', value: params.vMax });
    if (params.vMin) fields.push({ label: 'Min. Wert', value: params.vMin });
    if (params.eqMax) fields.push({ label: 'Max. gleiche Werte', value: params.eqMax });
    if (params.gapMax) fields.push({ label: 'Max. Lücke', value: params.gapMax });
    return { fields };
  }, [params]);
  usePublishChatContext(chatContext);

  const upload = useAnomalyUpload();
  const pipeline = useAnomalyPipeline();
  const { state } = pipeline;
  const download = useAnomalyDownload(state, t);
  const socketProgress = useAnomalySocket(state.uploadId);
  const progressForTracker = useAnomalyProgress({ state, upload, socket: socketProgress });
  const showOverlay = progressForTracker !== null;

  const [staleWarning, setStaleWarning] = useState<string | null>(null);

  useEffect(() => {
    const isApplying =
      state.status === 'applying_stl' ||
      state.status === 'applying_lstm' ||
      state.status === 'running';
    if (!isApplying || (socketProgress?.lastProgressAt ?? null) === null) {
      setStaleWarning(null);
      return;
    }
    const handle = setInterval(() => {
      const age = Date.now() - (socketProgress?.lastProgressAt ?? Date.now());
      if (age > STALE_PROGRESS_MS) {
        setStaleWarning(
          t('anomaly.errors.connectionUnstable', {
            defaultValue: 'Connection unstable. The pipeline may still be running on the server.',
          }),
        );
      } else {
        setStaleWarning(null);
      }
    }, 5_000);
    return () => clearInterval(handle);
  }, [state.status, socketProgress?.lastProgressAt, t]);

  const [localError, setLocalError] = useState<string | null>(null);
  const [paramShapeError, setParamShapeError] = useState<string | null>(null);
  const [hasParamErrors, setHasParamErrors] = useState(false);
  const [preSubmitError, setPreSubmitError] = useState<string | null>(null);

  // Step inference for the WorkflowStepper.
  const currentStep =
    state.status === 'idle' || state.status === 'uploading' ? 1
    : state.status === 'loaded' || state.status === 'configuring' ? 2
    : state.status === 'complete' ? 4
    : 3;
  const completedSteps = (
    state.status === 'idle' ? []
    : state.status === 'uploading' ? []
    : ['loaded', 'configuring', 'running', 'awaiting_stl_threshold',
       'applying_stl', 'awaiting_lstm_threshold', 'applying_lstm',
       'complete', 'error'].includes(state.status) ? [1]
    : []
  ).concat(
    ['running', 'awaiting_stl_threshold', 'applying_stl',
     'awaiting_lstm_threshold', 'applying_lstm', 'complete'].includes(state.status)
      ? [2] : [],
  ).concat(state.status === 'complete' ? [3] : []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    log.click('file picked', { fileName: f.name, sizeKB: Math.round(f.size / 1024) });
    setSelectedFile(f);
    setLocalError(null);
    upload.reset();
    pipeline.reset();
    const result = await upload.upload(f);
    if (!result) return;
    await pipeline.load(result.uploadId, result.filename, lang);
  }, [upload, pipeline, lang]);

  const handleRemoveFile = useCallback(() => {
    log.click('Restart');
    setSelectedFile(null);
    pipeline.reset();
    upload.reset();
    setLocalError(null);
    setParams(ANOMALY_DEFAULTS);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [pipeline, upload]);

  const handleProcess = useCallback(async () => {
    log.click('Daten verarbeiten', { params });
    setSuccess(null);
    if (hasParamErrors) {
      setPreSubmitError(
        t('anomaly.errors.fixParamsFirst', {
          defaultValue: 'Please correct the highlighted parameter errors before starting.',
        }),
      );
      return;
    }
    const quota = await canProcess();
    if (!quota.allowed) {
      setUpgradeMessage(t('errors.processingLimitReached', { defaultValue: 'Processing limit reached.' }));
      setUpgradeOpen(true);
      return;
    }
    await pipeline.start(params, lang);
    refreshUsage();
  }, [pipeline, params, lang, canProcess, refreshUsage, t, hasParamErrors]);

  // Show a success banner once on completion.
  useEffect(() => {
    if (state.status === 'complete') {
      setSuccess(t('processingComplete', { defaultValue: 'Processing complete' }));
    }
  }, [state.status, t]);

  // Clear the page-level selectedFile whenever the pipeline returns to `idle`
  // from another state — e.g. after an upload error is dismissed (DISMISS_ERROR
  // with no loaded data) or a restart. Otherwise FileUploadSection keeps the
  // drop zone hidden (hideDropZoneWhenFileSelected + a non-empty selectedFiles)
  // and the user is stranded on step 1 with no way to pick a file. The picker is
  // only reachable at `idle`, so the normal pick→upload sequence (idle→idle, no
  // status change) never triggers this.
  const prevStatusRef = useRef(state.status);
  useEffect(() => {
    if (state.status === 'idle' && prevStatusRef.current !== 'idle') {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    prevStatusRef.current = state.status;
  }, [state.status]);

  const showOriginalPlots = state.plots.original && state.plots.slope;
  const isUploading = upload.uploading;
  const isRunning =
    state.status === 'running' ||
    state.status === 'applying_stl' ||
    state.status === 'applying_lstm';

  const longOpInFlight =
    upload.uploading ||
    ['running', 'applying_stl', 'applying_lstm'].includes(state.status);
  useKeepAlive(longOpInFlight);

  return (
    <ErrorBoundary>
      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        resourceType="processing"
        message={upgradeMessage}
      />
      <div className="page-container" role="main" aria-label="Anomaly Detection">
        <div className="messages-container" role="region" aria-live="polite">
          <div className="messages-wrapper">
            {state.status === 'error' && (state.errorMessage || state.errorCode) && (
              <ErrorMessage
                message={(() => {
                  const mapped = mapAdjustmentsBackendError({
                    code: state.errorCode ?? undefined,
                    message: state.errorMessage ?? undefined,
                    suggestion: state.errorSuggestion ?? undefined,
                  });
                  if (mapped.translationKey) {
                    return t(`dataAdjustments:${mapped.translationKey}`, {
                      defaultValue: mapped.fallbackMessage,
                    });
                  }
                  return mapped.fallbackMessage || state.errorMessage || '';
                })()}
                onClose={() => pipeline.dispatch({ type: 'DISMISS_ERROR' })}
                duration={state.errorPersistent ? 0 : AUTO_DISMISS_ERROR_MS}
              />
            )}
            {state.status === 'error' && state.errorCode === 'TIME_GRID_REQUIRED' && (
              <div className="bg-accent border border-primary/20 rounded p-3 my-2 text-sm" role="alert">
                <p className="text-accent-foreground mb-2">
                  {t('errors.timeGridHint', { defaultValue: 'Configure your data on the Data Adjustments page first, then return here.' })}
                </p>
                <Link to={ROUTES.DATA_ADJUSTMENTS} className="text-primary underline hover:text-primary/80">
                  {t('errors.timeGridLink', { defaultValue: 'Open Data Adjustments →' })}
                </Link>
              </div>
            )}
            {localError && (
              <ErrorMessage
                message={localError}
                onClose={() => setLocalError(null)}
                duration={AUTO_DISMISS_ERROR_MS}
              />
            )}
            {upload.error && (
              <ErrorMessage
                message={upload.error}
                onClose={() => upload.reset()}
                duration={AUTO_DISMISS_ERROR_MS}
              />
            )}
            {paramShapeError && (
              <ErrorMessage
                message={paramShapeError}
                onClose={() => setParamShapeError(null)}
                duration={AUTO_DISMISS_ERROR_MS}
              />
            )}
            <ErrorMessage
              message={preSubmitError}
              onClose={() => setPreSubmitError(null)}
              duration={AUTO_DISMISS_ERROR_MS}
            />
            <ErrorMessage
              message={staleWarning}
              onClose={() => setStaleWarning(null)}
              duration={0}
            />
            {success && (
              <SuccessMessage
                message={success}
                onClose={() => setSuccess(null)}
                duration={AUTO_DISMISS_SUCCESS_MS}
              />
            )}
          </div>
        </div>

        <NetworkStatusIndicator />

        <div className="main-container relative">
          <nav aria-label="Workflow progress">
            <WorkflowStepper
              steps={WORKFLOW_STEPS}
              currentStep={currentStep}
              completedSteps={completedSteps}
              namespace="dataProcessing"
            />
          </nav>

          <div className="card max-w-7xl mx-auto bg-card p-8 shadow-lg rounded-lg">
            {/* File upload */}
            <section aria-label="File upload">
              <FileUploadSection
                fileInputRef={fileInputRef}
                onFileChange={handleFileChange}
                firstTwoLines={{}}
                selectedFiles={selectedFile ? [selectedFile] : []}
                multiple={false}
                accept=".csv"
                onRemoveFile={handleRemoveFile}
                dropLabelKeys={{
                  drop: 'upload.dropFile',
                  dropOr: 'upload.dropFileOr',
                  namespace: 'dataProcessing',
                }}
                hideDropZoneWhenFileSelected
                hideSelectedFilesHeader
                disabled={isRunning}
                disabledHint={isRunning ? t('anomaly.errors.fileInputDisabledDuringRun') : undefined}
              />
              {showOverlay && (
                <div
                  className="fixed inset-0 bg-background/95 z-[9999] flex items-center justify-center"
                  role="status"
                  aria-live="polite"
                  data-testid="processing-overlay"
                >
                  <ProgressTracker progress={progressForTracker} />
                </div>
              )}
            </section>

            {/* Original + collapsible slope */}
            {showOriginalPlots && (
              <>
                <LoadedFileInfo
                  filename={state.filename}
                  timestepMin={state.timestepMin}
                  offsetMin={state.offsetMin}
                />
                <OriginalDataSection
                  original={state.plots.original!}
                  slope={state.plots.slope!}
                />
              </>
            )}

            {/* Parameter form */}
            {state.status !== 'idle' && state.status !== 'uploading' && (
              <section aria-label="Anomaly parameters">
                <h2 className="text-base sm:text-lg font-semibold text-foreground mb-4 flex items-center">
                  {t('navBar.datenbearbeitung', { ns: 'navigation' })}
                  <InfoTooltip text={t('tooltips.section')} width="w-96" />
                </h2>
                <AnomalyParametersSection
                  parameters={params}
                  onChange={setParams}
                  lang={lang}
                  uploadId={state.uploadId}
                  onParamShapeError={() =>
                    setParamShapeError(
                      t('anomaly.errors.unexpectedResponse', {
                        defaultValue: 'The server returned unexpected data. Please reload the page; if the problem persists, contact support.',
                      }),
                    )
                  }
                  disabled={
                    state.status === 'running' ||
                    state.status === 'applying_stl' ||
                    state.status === 'applying_lstm'
                  }
                  onValidationStateChange={setHasParamErrors}
                />
              </section>
            )}

            {/* Action buttons */}
            {state.uploadId && state.status !== 'idle' && state.status !== 'uploading' && (
              <section className="mt-8 flex gap-3" aria-label="Actions">
                <SubmitButton
                  onClick={handleProcess}
                  disabled={isRunning || isUploading || state.status === 'awaiting_stl_threshold' || state.status === 'awaiting_lstm_threshold'}
                >
                  {t('processData')}
                </SubmitButton>
                <RestartButton
                  onRestart={handleRemoveFile}
                >
                  {t('restart')}
                </RestartButton>
                {download.downloadUrl && (
                  <button
                    type="button"
                    onClick={download.handleDownload}
                    disabled={download.downloading}
                    data-testid="download-csv"
                    className="rounded border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
                  >
                    {download.downloading ? '…' : t('anomaly.actions.downloadCsv')}
                  </button>
                )}
              </section>
            )}

            <ErrorMessage
              message={download.downloadError}
              onClose={download.clearDownloadError}
              duration={AUTO_DISMISS_ERROR_MS}
            />

            {/* Pause modals + progress banners */}
            <PipelineRunner pipeline={pipeline} lang={lang} />

            {/* Final results: STL/LSTM overlays + processed plot + Loading processed + Download */}
            <AnomalyResultsSection
              pipeline={pipeline}
              lang={lang}
              onUseProcessedSuccess={() => setSuccess(t('useProcessedSuccess', {
                defaultValue: 'Processed data is now your original. Configure new parameters and run again.'
              }))}
            />
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

AnomalyDetectionPage.displayName = 'AnomalyDetectionPage';
export default AnomalyDetectionPage;
